import { useState, type ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { FaArrowDown, FaArrowLeft, FaArrowRight, FaArrowUp, FaEllipsisV, FaTimes } from "react-icons/fa";
import { resolvePath, sortableColumn } from "../definition";
import { columnHeader } from "../labels";
import type { Catalog, ReportSpec, SortDirection } from "../types";
import { chip, chipActive, chipNeutral, chipWarning, ghostButton, input } from "./styles";

type Props = {
  catalog: Catalog;
  definition: ReportSpec;
  onMove: (from: number, to: number) => void;
  onRemove: (path: string) => void;
  onRename: (path: string, label: string) => void;
  onSort: (path: string, direction: SortDirection | null) => void;
};

/** The chosen columns in order, each with a small menu: move, sort, rename, remove. */
export default function ColumnBar({ catalog, definition, onMove, onRemove, onRename, onSort }: Props): ReactElement | null {
  const { t } = useTranslation();
  const [openPath, setOpenPath] = useState<string | null>(null);
  const [renaming, setRenaming] = useState("");

  if (definition.columns.length === 0) return null;

  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
        {t("reports.builder.columns", { defaultValue: "Columns" })}
      </span>
      {definition.columns.map((column, index) => {
        const resolved = resolvePath(catalog, definition.source, column.path);
        const header = columnHeader(t, catalog, definition.source, column);
        const sort = definition.sort.find((item) => item.path === column.path);
        const open = openPath === column.path;
        return (
          <div key={column.path} className="relative">
            <button
              type="button"
              aria-haspopup="menu"
              aria-expanded={open}
              onClick={() => {
                setOpenPath(open ? null : column.path);
                setRenaming(column.label ?? "");
              }}
              className={`${chip} ${resolved ? (sort ? chipActive : chipNeutral) : chipWarning}`}
            >
              <span className="truncate">{header}</span>
              {sort && (sort.direction === "ASC" ? <FaArrowUp aria-hidden /> : <FaArrowDown aria-hidden />)}
              <FaEllipsisV aria-hidden className="text-[10px] opacity-60" />
            </button>
            {open && (
              <div
                role="menu"
                className="absolute left-0 z-40 mt-2 w-64 space-y-2 rounded-[22px] border border-slate-200 bg-white p-3 shadow-xl dark:border-white/10 dark:bg-slate-900"
              >
                <div className="flex items-center gap-1">
                  <button type="button" className={ghostButton} disabled={index === 0}
                          onClick={() => onMove(index, index - 1)}>
                    <FaArrowLeft aria-hidden /> {t("reports.builder.move_left", { defaultValue: "Left" })}
                  </button>
                  <button type="button" className={ghostButton} disabled={index === definition.columns.length - 1}
                          onClick={() => onMove(index, index + 1)}>
                    {t("reports.builder.move_right", { defaultValue: "Right" })} <FaArrowRight aria-hidden />
                  </button>
                </div>
                {sortableColumn(resolved) && (
                  <div className="flex flex-wrap items-center gap-1">
                    <button type="button" className={ghostButton} onClick={() => onSort(column.path, "ASC")}>
                      <FaArrowUp aria-hidden /> {t("reports.builder.sort_asc", { defaultValue: "Sort ascending" })}
                    </button>
                    <button type="button" className={ghostButton} onClick={() => onSort(column.path, "DESC")}>
                      <FaArrowDown aria-hidden /> {t("reports.builder.sort_desc", { defaultValue: "Sort descending" })}
                    </button>
                    {sort && (
                      <button type="button" className={ghostButton} onClick={() => onSort(column.path, null)}>
                        {t("reports.builder.sort_off", { defaultValue: "No sorting" })}
                      </button>
                    )}
                  </div>
                )}
                <form
                  className="space-y-1"
                  onSubmit={(event) => {
                    event.preventDefault();
                    onRename(column.path, renaming);
                    setOpenPath(null);
                  }}
                >
                  <label className="block text-xs font-bold text-slate-600 dark:text-slate-300">
                    {t("reports.builder.column_name", { defaultValue: "Column name" })}
                    <input
                      value={renaming}
                      maxLength={80}
                      onChange={(event) => setRenaming(event.target.value)}
                      placeholder={columnHeader(t, catalog, definition.source, { path: column.path })}
                      className={`${input} mt-1`}
                    />
                  </label>
                </form>
                <button
                  type="button"
                  className={`${ghostButton} text-rose-600 dark:text-rose-300`}
                  onClick={() => {
                    onRemove(column.path);
                    setOpenPath(null);
                  }}
                >
                  <FaTimes aria-hidden /> {t("reports.builder.remove_column", { defaultValue: "Remove column" })}
                </button>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
